import {
    CustomerId,
    LangCode,
    VideoSourceCodec,
    VideoSourceEncryption,
    VideoSourceProtocol,
} from '@tivio/sdk-react'

import { ENV } from '../env'

import type { Config } from '@tivio/sdk-react'


type Platform = 'tizen' | 'webos'

type Configs = {
    [customer in CustomerId]?: {
        [platform in Platform]: Config
    }
}

const tivioConfig: Configs = {
    [CustomerId.MALL]: {
        webos: {
            secret: 'ad14f90431ff4e4f72ac951c8d5b09a9', // production, MALL.TV
            disableUnmounting: false,
            verbose: true,
            enableSentry: false,
            deviceCapabilities: [],
            currency: 'CZK',
            capabilitiesOptions: {
                /**
                 * On LG it is not possible to use HTTPS certificates with root authority
                 * `Let's Encrypt` (more info [here](https://jira.nangu.tv/browse/TIV-1047).
                 *
                 * Since cdn3.tiv.io content is using Letsencrypt certificates,
                 * I am forcing HTTP protocol as a workaround.
                 */
                preferHttp: true,
            },
            language: LangCode.CS,
            customerId: CustomerId.MALL,
        },
        tizen: {
            secret: 'e4EfP1cX6AGtP1WMP8UO', // production, MALL.TV
            disableUnmounting: false,
            verbose: true,
            deviceCapabilities: [],
            currency: 'CZK',
            language: LangCode.CS,
        },
    },
    [CustomerId.JOJ]: {
        webos: {
            secret: '5pec1alPr06303w3b05d',
            disableUnmounting: false,
            verbose: ENV.IS_DEBUG,
            enableSentry: false,
            /**
             * LG TV does not play HLS sources generated by JOJ platform. It does play other HLS sources, such as
             * ones from 5cent CDN. At this time there is no time to investigate further
             * (see https://jira.nangu.tv/browse/TIV-1073)
             */
            deviceCapabilities: [
                { codec: VideoSourceCodec.H264, protocol: VideoSourceProtocol.DASH, encryption: VideoSourceEncryption.NONE },
                { codec: VideoSourceCodec.H264, protocol: VideoSourceProtocol.DASH, encryption: VideoSourceEncryption.PLAYREADY },
                { codec: VideoSourceCodec.H264, protocol: VideoSourceProtocol.DASH, encryption: VideoSourceEncryption.WIDEVINE },
                // h265 may also be supported, we just haven't tried it yet...
            ],
            capabilitiesOptions: {
                /**
                 * On LG it is not possible to use HTTPS certificates with root authority
                 * `Let's Encrypt` (more info [here](https://jira.nangu.tv/browse/TIV-1047).
                 *
                 * Since JOJ content (images, streams) are using Letsencrypt certificates,
                 * I am forcing HTTP protocol as a workaround.
                 *
                 * We have the same problem also with cdn3.tiv.io, where on 5centsCDN we have configured
                 * a Let'sencrypt certificate
                 */
                preferHttp: true,
            },
            currency: 'EUR',
            language: LangCode.SK,
            customerId: CustomerId.JOJ,
        },
        tizen: {
            secret: ENV.IS_DEBUG ?
                '4539423519157f9cebd33c9d9c17c747' // production TV JOJ, debug secret
                : '7jvZZVFlD0PXFf9fyXtP', // production, TV JOJ
            disableUnmounting: false,
            verbose: ENV.IS_DEBUG,
            deviceCapabilities: [],
            currency: 'EUR',
            language: LangCode.SK,
        },
    },
    [CustomerId.OKTAGON]: {
        webos: {
            secret: '5pec1alPr060kT4gw3b0',
            disableUnmounting: false,
            verbose: ENV.IS_DEBUG,
            enableSentry: false,
            deviceCapabilities: [],
            currency: 'EUR',
            capabilitiesOptions: {
                /**
                 * On LG it is not possible to use HTTPS certificates with root authority
                 * `Let's Encrypt` (more info [here](https://jira.nangu.tv/browse/TIV-1047).
                 *
                 * Since cdn3.tiv.io content is using Letsencrypt certificates,
                 * I am forcing HTTP protocol as a workaround.
                 */
                preferHttp: true,
            },
            language: LangCode.CS,
            customerId: CustomerId.OKTAGON,
        },
        tizen: {
            secret: ENV.IS_DEBUG ?
                '59b4ca594b0c1e23ba673b638f2889e6' // production Oktagon.TV, debug secret
                : 'GjvTxnJDrH5CjU7x4JZA', // production, Oktagon.TV
            disableUnmounting: false,
            verbose: ENV.IS_DEBUG,
            deviceCapabilities: [],
            currency: 'EUR',
            language: LangCode.CS,
        },
    },
    [CustomerId.GARAZ]: {
        webos: {
            secret: 'VLBGqTtqqYiCKuSOM3Ae',
            disableUnmounting: false,
            verbose: false,
            enableSentry: false,
            deviceCapabilities: [],
            currency: 'EUR',
            capabilitiesOptions: {
                /**
                 * On LG it is not possible to use HTTPS certificates with root authority
                 * `Let's Encrypt` (more info [here](https://jira.nangu.tv/browse/TIV-1047).
                 *
                 * Since cdn3.tiv.io content is using Letsencrypt certificates,
                 * I am forcing HTTP protocol as a workaround.
                 */
                preferHttp: true,
            },
            language: LangCode.CS,
            customerId: CustomerId.GARAZ,
        },
        tizen: {
            secret: 'VLBGqTtqqYiCKuSOM3Ae',
            disableUnmounting: false,
            verbose: false,
            deviceCapabilities: [],
            currency: 'EUR',
            language: LangCode.CS,
        },
    },
    [CustomerId.DVTV]: {
        webos: {
            secret: 'ptvdvtvBerDNKBjGRFoY',
            disableUnmounting: false,
            verbose: false,
            enableSentry: false,
            /**
             * LG TV does not play HLS sources generated by JOJ platform.
             */
            deviceCapabilities: [
                { codec: VideoSourceCodec.H264, protocol: VideoSourceProtocol.DASH, encryption: VideoSourceEncryption.NONE },
                { codec: VideoSourceCodec.H264, protocol: VideoSourceProtocol.DASH, encryption: VideoSourceEncryption.PLAYREADY },
                { codec: VideoSourceCodec.H264, protocol: VideoSourceProtocol.DASH, encryption: VideoSourceEncryption.WIDEVINE },
                // h265 may also be supported, we just haven't tried it yet...
            ],
            currency: 'CZK',
            capabilitiesOptions: {
                /**
                 * On LG it is not possible to use HTTPS certificates with root authority
                 * `Let's Encrypt` (more info [here](https://jira.nangu.tv/browse/TIV-1047).
                 *
                 * Since cdn3.tiv.io content is using Letsencrypt certificates,
                 * I am forcing HTTP protocol as a workaround.
                 */
                preferHttp: true,
            },
            language: LangCode.CS,
            customerId: CustomerId.DVTV,
        },
        tizen: {
            secret: '9dTnqTiqaWTsqhVXwm4b',
            disableUnmounting: false,
            verbose: false,
            deviceCapabilities: [],
            currency: 'CZK',
            language: LangCode.CS,
        },
    },
    [CustomerId.STARMAX]: {
        webos: {
            secret: 'Ot2bwJgHnfLwbVcQ1b5X',
            disableUnmounting: false,
            verbose: false,
            enableSentry: false,
            deviceCapabilities: [],
            currency: 'CZK',
            language: LangCode.CS,
            customerId: CustomerId.STARMAX,
        },
        tizen: {
            secret: 'W0zBCI58Flp1PWmsSTfC',
            disableUnmounting: false,
            verbose: false,
            deviceCapabilities: [],
            currency: 'CZK',
            language: LangCode.CS,
        },
    },
    [CustomerId.STARGAZE]: {
        webos: {
            secret: 'UAfAVJdUxHth0GnHeZ8M',
            disableUnmounting: false,
            verbose: false,
            enableSentry: false,
            deviceCapabilities: [],
            currency: 'CZK',
            language: LangCode.CS,
            customerId: CustomerId.STARGAZE,
        },
        tizen: {
            secret: 'BaiKb05yt21IiQ3HW1qk',
            disableUnmounting: false,
            verbose: false,
            deviceCapabilities: [],
            currency: 'CZK',
            language: LangCode.CS,
        },
    },
}

export {
    tivioConfig,
}
